import { useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface EmailChip {
  readonly email: string;
  readonly valid: boolean;
}

interface EmailChipsInputProps {
  readonly value: readonly EmailChip[];
  readonly onChange: (next: EmailChip[]) => void;
  readonly disabled?: boolean;
  readonly maxChips?: number;
  readonly placeholder?: string;
  readonly autoFocus?: boolean;
}

function normalize(raw: string): string {
  return raw.trim().toLowerCase();
}

function makeChip(raw: string): EmailChip {
  const email = normalize(raw);
  return { email, valid: EMAIL_REGEX.test(email) };
}

export function EmailChipsInput({
  value,
  onChange,
  disabled = false,
  maxChips = 50,
  placeholder = 'name@company.com',
  autoFocus = false,
}: EmailChipsInputProps) {
  const [draft, setDraft] = useState('');
  const [duplicateFlash, setDuplicateFlash] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const atLimit = value.length >= maxChips;

  function commitDraft(text: string): boolean {
    const normalized = normalize(text);
    if (!normalized) return false;
    if (value.length >= maxChips) return false;
    if (value.some((c) => c.email === normalized)) {
      setDuplicateFlash(normalized);
      window.setTimeout(() => setDuplicateFlash((cur) => (cur === normalized ? null : cur)), 800);
      return true;
    }
    onChange([...value, makeChip(normalized)]);
    return true;
  }

  function commitMany(tokens: string[]): void {
    let next = [...value];
    const seen = new Set(next.map((c) => c.email));
    for (const t of tokens) {
      const normalized = normalize(t);
      if (!normalized) continue;
      if (next.length >= maxChips) break;
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      next.push(makeChip(normalized));
    }
    onChange(next);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === ',' || e.key === 'Enter' || e.key === 'Tab') {
      if (draft.trim().length > 0) {
        e.preventDefault();
        if (commitDraft(draft)) setDraft('');
      }
      return;
    }
    if (e.key === 'Backspace' && draft.length === 0 && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  }

  function handleBlur(): void {
    if (draft.trim().length > 0) {
      if (commitDraft(draft)) setDraft('');
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>): void {
    const text = e.clipboardData.getData('text');
    if (!/[\s,]/.test(text)) return;
    e.preventDefault();
    const tokens = text.split(/[\s,]+/).filter(Boolean);
    commitMany(tokens);
    setDraft('');
  }

  function removeChip(index: number): void {
    onChange(value.filter((_, i) => i !== index));
    inputRef.current?.focus();
  }

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 min-h-[34px] w-full px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus-within:ring-1 focus-within:ring-gray-900 cursor-text"
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((chip, i) => {
        const flashing = duplicateFlash === chip.email;
        const base =
          'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border transition-colors';
        const tone = chip.valid
          ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100'
          : 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900/60 text-red-700 dark:text-red-300';
        const flash = flashing ? 'ring-2 ring-amber-400' : '';
        return (
          <span
            key={`${chip.email}-${i}`}
            className={`${base} ${tone} ${flash}`}
            title={chip.valid ? chip.email : 'Invalid email address'}
          >
            {chip.email}
            <button
              type="button"
              aria-label={`Remove ${chip.email}`}
              onClick={(e) => {
                e.stopPropagation();
                removeChip(i);
              }}
              className="hover:text-gray-900 dark:hover:text-white"
              disabled={disabled}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      })}
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onPaste={handlePaste}
        disabled={disabled || atLimit}
        autoFocus={autoFocus}
        placeholder={value.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[120px] text-xs bg-transparent outline-none disabled:opacity-50"
      />
      {atLimit && (
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          Maximum {maxChips} emails
        </span>
      )}
    </div>
  );
}
